import {
    Form,
    Input,
    Button,
    Alert,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPassword } from '../../api/auth.api';
import { getErrorMessage } from '../../utils/messages';

const ConfirmForm = () => {
    const { t } = useTranslation('login');

    const [params] = useSearchParams();
    const passwordResetToken = params.get('token');

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const navigate = useNavigate();

    const handleSubmit = ({ newPassword }: { newPassword: string }) => {
        setLoading(true);
        setError(null);
        setSuccess(false);

        const payload = {
            newPassword,
            passwordResetToken,
        };

        confirmPassword(payload)
            .then(() => {
                setSuccess(true);
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            })
            .catch((error) => {
                const description = getErrorMessage(error.message);
                setError(t(description));
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!passwordResetToken) {
            setError(t('NOT_FOUND_TOKEN'));
        }
    }, [passwordResetToken, t]);

    return (
        <Form onFinish={handleSubmit} layout="vertical">
            {
                (error) && (<Alert message={error} type="error" style={{ marginBottom: 24 }} />)
            }
            {
                (success) && (<Alert message={t('RESET_PASSWORD_SUCCESS')} type="success" style={{ marginBottom: 24 }} />)
            }
            <Form.Item
                name="password"
                label={t('NEW_PASSWORD')}
                rules={[{ required: true, message: t('REQUIRED') }]}
            >
                <Input.Password
                    disabled={success || loading || !passwordResetToken}
                    placeholder={t('NEW_PASSWORD')}
                />
            </Form.Item>
            <Form.Item
                name="newPassword"
                dependencies={['password']}
                label={t('REPEAT_PASSWORD')}
                rules={[
                    { required: true, message: t('REQUIRED') },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error(t('PASSWORDS_NOT_MATCH')));
                        },
                    }),
                ]}
            >
                <Input.Password
                    disabled={success || loading || !passwordResetToken}
                    placeholder={t('REPEAT_PASSWORD')}
                />
            </Form.Item>
            <Button
                type="primary"
                htmlType="submit"
                block
                loading={loading}
                disabled={!passwordResetToken}
            >
                {t('RESET_PASSWORD')}
            </Button>
        </Form>
    );
};

export default ConfirmForm;
